import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Download, ArrowRight, Loader2, XCircle } from 'lucide-react';

import { API } from '@/lib/apiBase';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const { clearCart } = useCart();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');
  const freeOrderId = searchParams.get('order_id');
  const isFree = searchParams.get('free') === '1';

  useEffect(() => {
    // Free (€0) order: no Stripe polling needed — already marked paid server-side.
    if (isFree && freeOrderId) {
      setStatus('success');
      setPaymentData({
        currency: 'chf',
        amount_total: 0,
        payment_status: 'paid',
        order_id: freeOrderId,
        is_free: true,
      });
      clearCart();
      return;
    }

    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }

      try {
        const headers = { ...getAuthHeaders() };
        const response = await fetch(`${API}/checkout/status/${sessionId}?lang=${language}`, {
          headers, credentials: 'include'
        });

        if (!response.ok) throw new Error('Failed to check status');

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }

        attempts++;
        setTimeout(pollPaymentStatus, pollInterval);
      } catch (error) {
        attempts++;
        setTimeout(pollPaymentStatus, pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, getAuthHeaders, clearCart, language, freeOrderId, isFree]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-12" data-testid="checkout-success">
      {status === 'checking' && (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">{t('checkout.processing')}</h2>
            <p className="text-muted-foreground">{t('checkout.pleaseWait')}</p>
          </CardContent>
        </Card>
      )}

      {status === 'success' && (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">{t('checkout.success')}</h2>
            <p className="text-muted-foreground mb-8">{t('checkout.thankYou')}</p>

            {paymentData && (
              <div className="bg-muted/50 rounded-sm p-4 mb-8 font-mono text-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">{t('checkout.amountPaid')}</span>
                  <span className="font-bold">
                    {paymentData.currency?.toUpperCase()} {(paymentData.amount_total / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('checkout.status')}</span>
                  <span className="text-green-500 font-bold uppercase">{paymentData.payment_status}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-primary hover:bg-primary/90">
                <Link to="/orders">
                  <Download className="w-4 h-4 mr-2" />
                  {t('checkout.viewDownloads')}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/gallery">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {t('checkout.continueShopping')}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {status === 'failed' && (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">{t('checkout.failed')}</h2>
            <p className="text-muted-foreground mb-8">{t('checkout.failedDesc')}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-primary hover:bg-primary/90">
                <Link to="/cart">{t('checkout.tryAgain')}</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/dashboard">{t('checkout.backToDashboard')}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
